<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      勤怠変更申請詳細
    </Header>
    <ApplicationCard
      v-if="application"
      :application="application"
      class="mt-3 mx-3 mb-2"
    />

    <div class="mx-3 my-3" v-if="attendanceCorrection">
      <p class="text-size-14 mb-2">申請内容</p>
      <div class="bg-white border border-outline-color p-3 round-4">
        <div class="d-flex justify-content-between">
          <p class="text-mild text-size-14">勤務時間</p>
          <p class="text-mild text-size-14">
            {{ actualStartedWorkAt }} - {{ actualFinishedWorkAt }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <p class="text-mild text-size-14">休憩時間</p>
          <p
            class="text-mild text-size-14"
            v-if="actualStartedRestAt && actualFinishedRestAt"
          >
            {{ actualStartedRestAt }} - {{ actualFinishedRestAt }}
          </p>
          <p class="text-mild text-size-14" v-else>休憩無し</p>
        </div>
      </div>

      <div class="bg-white border border-outline-color p-3 mt-3 round-4">
        <div class="d-flex justify-content-between">
          <p class="text-mild text-size-14">規定の給与</p>
          <p class="text-mild text-size-14">
            ¥
            {{ formatNumber(salaryPerDay) }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <p class="text-mild text-size-14">交通費</p>
          <p class="text-mild text-size-14">
            ¥ {{ formatNumber(variousAllowances) }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <p class="text-mild text-size-14 text-weight-500">規定額合計</p>
          <p class="text-mild text-size-14 text-weight-500">
            ¥ {{ formatNumber(salaryPerDay + variousAllowances) }}
          </p>
        </div>
        <div
          class="d-flex justify-content-between border-top border-outline-color mt-2"
        >
          <p class="text-mild text-size-14 mt-2">変更後の給与</p>
          <p class="text-mild text-size-14 mt-2">
            ¥ {{ formatNumber(correctedSalary) }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <p class="text-mild text-size-14">交通費</p>
          <p class="text-mild text-size-14">
            ¥ {{ formatNumber(variousAllowances) }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <p class="text-mild text-size-14 text-weight-500">変更申請額合計</p>
          <p class="text-mild text-size-14 text-weight-500">
            ¥
            {{ formatNumber(correctedSalary + variousAllowances) }}
          </p>
        </div>
        <div
          class="d-flex justify-content-between border-top border-outline-color mt-2"
        >
          <p class="text-size-16 text-weight-500 mt-2">差額</p>
          <p class="text-blue text-size-16 text-weight-500 mt-2">
            ¥ {{ formatNumber(differenceSalaryPerDay) }}
          </p>
        </div>
        <div class="mt-3">
          <p class="text-mild text-size-12">
            報酬額が9300円以上(交通費除く)の場合、別途源泉所得税が控除されます
          </p>
        </div>
      </div>

      <div class="mt-3" v-if="workerComment">
        <p class="text-size-14 mb-2">申請コメント</p>
        <div class="bg-white border border-outline-color p-3 round-4">
          <p class="text-size-14 comment" v-html="workerComment" />
        </div>
      </div>
      <div class="mt-3" v-if="status !== 'submitted' && officeComment">
        <p class="text-size-14 mb-2">事務所コメント</p>
        <div class="bg-white border border-outline-color p-3 round-4">
          <p class="text-size-14 comment" v-html="officeComment" />
        </div>
      </div>

      <AppButton
        v-if="status === 'rejected'"
        block
        class="mt-4"
        variant="green"
        :to="{
          name: 'AttendanceCorrection',
          params: { applicationId }
        }"
      >
        再申請する
      </AppButton>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import Header from "@/components/organisms/common/Header.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import ApplicationCard from "@/components/organisms/applications/ApplicationCard.vue";
import { applicationService } from "@/app/services/ApplicationService";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Application } from "@/types/order/Application";
import { formatNumber } from "@/utils/number";
import { formatFromISOString } from "@/utils/date";

@Component({
  components: {
    Header,
    AppButton,
    FeatherIcon,
    ApplicationCard
  }
})
export default class AttendanceCorrectionDetail extends Vue {
  application: Application | null = null;

  get applicationId() {
    return this.$route.params.applicationId;
  }

  get attendanceCorrection() {
    return this.application &&
      this.application.order &&
      this.application.attendanceCorrection
      ? this.application.attendanceCorrection
      : null;
  }

  get status() {
    return this.attendanceCorrection?.status || "";
  }

  get correctedSalary() {
    return this.attendanceCorrection?.correctedSalary || 0;
  }

  get workerComment() {
    return this.attendanceCorrection?.workerComment || "";
  }

  get officeComment() {
    return this.attendanceCorrection?.officeComment || "";
  }

  get actualStartedWorkAt() {
    return this.attendanceCorrection?.actualStartedWorkAt
      ? formatFromISOString(
          this.attendanceCorrection.actualStartedWorkAt,
          "HH:mm"
        )
      : "";
  }

  get actualFinishedWorkAt() {
    return this.attendanceCorrection?.actualFinishedWorkAt
      ? formatFromISOString(
          this.attendanceCorrection.actualFinishedWorkAt,
          "HH:mm"
        )
      : "";
  }

  get actualStartedRestAt() {
    return this.attendanceCorrection?.actualStartedRestAt
      ? formatFromISOString(
          this.attendanceCorrection.actualStartedRestAt,
          "HH:mm"
        )
      : "";
  }

  get actualFinishedRestAt() {
    return this.attendanceCorrection?.actualFinishedRestAt
      ? formatFromISOString(
          this.attendanceCorrection.actualFinishedRestAt,
          "HH:mm"
        )
      : "";
  }

  get salaryPerDay() {
    return this.application ? this.application.appliedSalaryPerDay : 0;
  }

  get variousAllowances() {
    return this.application ? this.application.variousAllowances : 0;
  }

  get differenceSalaryPerDay() {
    return this.correctedSalary - this.salaryPerDay;
  }

  async created() {
    this.$loading.show();
    try {
      await this.fetchApplication();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchApplication() {
    this.application = await applicationService.fetchApplication(
      Number(this.applicationId)
    );
  }

  onClickBack() {
    this.$router.back();
  }

  formatNumber(value: number) {
    return formatNumber(value);
  }
}
</script>

<style lang="scss" scoped>
.comment {
  white-space: pre-wrap;
}
</style>
